import React, {
    useEffect, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    Input
} from 'shared/components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Loader2, AlertCircle
} from 'lucide-react';
import {
    cn
} from 'shared/lib/utils';
import {
    mcpDiscoveryService
} from 'shared/api/services/BEX/mcpDiscovery.service';
import McpConnectionPicker from '../inline-pickers/McpConnectionPicker';
import LongTextField from '../../primitives/LongTextField';
import FieldHelp from '../components/FieldHelp';
import ResourcePicker from '../../components/ResourcePicker';
import {
    KeyValueOelEditor, StepCommonAdvanced, StepCommonInlineFields,
    useProcedureStepIds
} from './_shared';
import {
    LlmStepZone, LlmLabeled, LlmStepCanvas, llmFocusInputClass
} from './llmStepLayout';

/**
 * InlineToolStep — kind:"tool".
 * Invokes a tool via a protocol adapter.
 */
export default function InlineToolStep({
    step, onPatchStep, oelContext
}) {
    const {
        oragID, subOragID
    } = useParams();
    const protocol = step.protocol || 'mcp';
    const toolRef = step.tool_ref || {};
    const inputs = step.inputs || {};
    const outputSchema = step.output_schema;

    const [mcpTools, setMcpTools] = useState([]);
    const [discovering, setDiscovering] = useState(false);
    const [discoverErr, setDiscoverErr] = useState(null);

    const patch = (p) => onPatchStep({ ...step, ...p });
    const patchToolRef = (p) => patch({
        tool_ref: { ...toolRef, ...p }
    });

    function setProtocol(next) {
        patch({ protocol: next, tool_ref: {} });
    }

    useEffect(() => {
        if (protocol !== 'mcp' || !toolRef.connection_id || !oragID || !subOragID) {
            setMcpTools([]);
            return undefined;
        }
        let cancelled = false;
        setDiscovering(true);
        setDiscoverErr(null);
        mcpDiscoveryService.discover({
            org_id: oragID,
            sub_org_id: subOragID,
            connection_id: toolRef.connection_id
        })
            .then((res) => {
                if (cancelled) return;
                setMcpTools(Array.isArray(res?.tools) ? res.tools : []);
            })
            .catch((err) => {
                if (cancelled) return;
                setDiscoverErr(err?.response?.data?.message || err?.message || 'Discovery failed');
            })
            .finally(() => { if (!cancelled) setDiscovering(false); });
        return () => { cancelled = true; };
    }, [protocol, toolRef.connection_id, oragID, subOragID]);

    const stepIdOptions = useProcedureStepIds(oelContext);
    const inputClass = cn('h-8 text-[12.5px]', llmFocusInputClass);

    return (
        <LlmStepCanvas>
            <LlmStepZone
                variant="configuration"
                title="Protocol"
                description="How this step reaches the tool — MCP server or another opcode."
            >
                <LlmLabeled
                    label="Protocol"
                    help="mcp: call a tool on a connected MCP server. opcode_as_tool: expose another opcode as a tool (same sub-org)."
                >
                    <Select value={protocol} onValueChange={setProtocol}>
                        <SelectTrigger className={inputClass} aria-label="tool protocol">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="mcp">mcp — call an MCP-server tool</SelectItem>
                            <SelectItem value="opcode_as_tool">opcode_as_tool — call another opcode as a tool</SelectItem>
                        </SelectContent>
                    </Select>
                </LlmLabeled>
            </LlmStepZone>

            {protocol === 'mcp' && (
                <>
                    <LlmStepZone
                        variant="configuration"
                        title="MCP connection"
                        description="Pick a saved connection — tools are discovered automatically."
                    >
                        <FieldHelp>
                            The saved MCP connection defines server URL, auth, and timeout. Tools are
                            discovered automatically after you pick a connection.
                        </FieldHelp>
                        <McpConnectionPicker
                            connectionId={toolRef.connection_id || ''}
                            onChange={(v) => patchToolRef({
                                connection_id: v?.connection_id || v?._id || '',
                                tool_name: ''
                            })}
                        />
                    </LlmStepZone>

                    <LlmStepZone
                        variant="configuration"
                        title="Tool"
                        description={discovering ? 'Discovering tools…' : `${mcpTools.length} tool(s) discovered`}
                    >
                        {!toolRef.connection_id ? (
                            <div className="rounded-md border border-dashed bg-white/60 px-3 py-2 text-[11.5px] text-muted-foreground">
                                Pick a connection above first.
                            </div>
                        ) : discovering ? (
                            <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                                <Loader2 className="h-3 w-3 animate-spin" /> discovering tools…
                            </div>
                        ) : discoverErr ? (
                            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-[11.5px] text-destructive">
                                <AlertCircle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                                <span>{discoverErr} — you can still type a tool name manually.</span>
                            </div>
                        ) : mcpTools.length === 0 ? (
                            <div className="rounded-md border border-dashed bg-white/60 px-3 py-2 text-[11.5px] text-muted-foreground">
                                No tools discovered. Type the tool name manually below.
                            </div>
                        ) : (
                            <LlmLabeled
                                label="Tool name"
                                help="Pick from tools discovered on the selected MCP connection."
                            >
                                <Select
                                    value={toolRef.tool_name || ''}
                                    onValueChange={(v) => patchToolRef({ tool_name: v })}
                                >
                                    <SelectTrigger className={inputClass}>
                                        <SelectValue placeholder="Pick a tool" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {mcpTools.map((t) => (
                                            <SelectItem key={t.name} value={t.name}>
                                                <span className="font-mono text-[12px]">{t.name}</span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </LlmLabeled>
                        )}
                        <LlmLabeled label="Tool name (manual)" help="Overrides the picker if both set">
                            <Input
                                value={toolRef.tool_name || ''}
                                onChange={(e) => patchToolRef({ tool_name: e.target.value })}
                                placeholder="e.g. enqueue_review"
                                className={cn(inputClass, 'font-mono')}
                            />
                        </LlmLabeled>
                    </LlmStepZone>
                </>
            )}

            {protocol === 'opcode_as_tool' && (
                <LlmStepZone
                    variant="configuration"
                    title="OpCode reference"
                    description="Call another opcode in this sub-org as a tool."
                >
                    <LlmLabeled label="Tool name (opcode_id)" help="Must be an opcode in the same sub-org">
                        <ResourcePicker
                            kind="opcode"
                            value={toolRef.tool_name || ''}
                            onChange={(id) => patchToolRef({ tool_name: id })}
                            placeholder="No opcode selected"
                            helpText="Calls the picked opcode as a tool. Only opcodes in this sub-org's Library are listed."
                        />
                    </LlmLabeled>
                </LlmStepZone>
            )}

            <LlmStepZone
                variant="arguments"
                title="Arguments"
                description="Key/value pairs passed to the tool — values are OEL expressions."
            >
                <KeyValueOelEditor
                    value={inputs}
                    onChange={(next) => patch({ inputs: next })}
                    keyLabel="Argument"
                    addLabel="Add argument"
                    placeholderKey="arg_name"
                    placeholderValue="@input.field"
                    oelContext={oelContext}
                />
            </LlmStepZone>

            <LlmStepZone
                variant="output"
                title="Output schema"
                description="Optional JSON Schema describing the tool response shape."
            >
                <LlmLabeled
                    label="Output schema"
                    help="Optional JSON Schema describing the tool response shape — helps downstream OEL pickers suggest nested paths."
                >
                    <LongTextField
                        label="output_schema"
                        value={typeof outputSchema === 'string' ? outputSchema : (outputSchema ? JSON.stringify(outputSchema, null, 2) : '')}
                        onChange={(v) => {
                            if (!v.trim()) { patch({ output_schema: undefined }); return; }
                            try { patch({ output_schema: JSON.parse(v) }); }
                            catch { patch({ output_schema: v }); }
                        }}
                        placeholder='{ "type": "object", "properties": {} }'
                        monospace
                    />
                </LlmLabeled>
            </LlmStepZone>

            <StepCommonInlineFields
                step={step}
                onPatchStep={onPatchStep}
                stepIdOptions={stepIdOptions}
            />

            <StepCommonAdvanced
                step={step}
                onPatchStep={onPatchStep}
                oelContext={oelContext}
            />
        </LlmStepCanvas>
    );
}
